// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useRef, useState, type ReactNode, type TouchEvent } from "react";
import { Spin } from "antd";

// PullToRefresh is the scroll container for a mobile list: a downward drag while scrolled to the top
// reveals a spinner and, past a threshold on release, runs onRefresh(). Damped + overscroll-contained
// so it doesn't fight the native scroll. No-op on the desktop shell (touch only).
const THRESHOLD = 64;

export function PullToRefresh({
  onRefresh,
  className,
  children,
}: {
  readonly onRefresh: () => Promise<void> | void;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const [dist, setDist] = useState(0);
  const [busy, setBusy] = useState(false);
  const startY = useRef(-1);
  const el = useRef<HTMLDivElement>(null);

  const atTop = () => (el.current?.scrollTop ?? 1) <= 0;

  function onStart(e: TouchEvent) {
    startY.current = atTop() ? e.touches[0].clientY : -1;
  }
  function onMove(e: TouchEvent) {
    if (startY.current < 0 || busy) return;
    const dy = e.touches[0].clientY - startY.current;
    setDist(dy > 0 && atTop() ? Math.min(dy * 0.45, THRESHOLD * 1.4) : 0);
  }
  async function onEnd() {
    if (dist >= THRESHOLD && !busy) {
      setBusy(true);
      try {
        await onRefresh();
      } finally {
        setBusy(false);
      }
    }
    setDist(0);
    startY.current = -1;
  }

  const show = busy || dist > 6;
  return (
    <div ref={el} className={className} style={{ overscrollBehavior: "contain" }} onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} onTouchCancel={onEnd}>
      <div className="ptr-indicator" style={{ height: busy ? 44 : dist, opacity: show ? 1 : 0 }}>
        {show && <Spin size="small" />}
      </div>
      {children}
    </div>
  );
}
